import React, { useState } from 'react';
import { AlertCircle, Eye, EyeOff, KeyRound, Loader2, Lock, Shield } from 'lucide-react';
import {
  BRAND_PORTAL,
  clearPortalPbSession,
  getPortalPocketBaseUrl,
  getPortalPbToken,
  persistPortalPbSession,
} from '../../constants';
import type { CustomerRecord } from '../CustomerLogin';
import {
  PORTAL_TEMP_LOGIN_PW_KEY,
  buildCustomerRecord,
  persistPortalCustomer,
} from './portalCustomerAccess';

export interface MustChangePasswordOverlayProps {
  customer: CustomerRecord;
  onComplete: (updated: CustomerRecord) => void;
  /** PW gesetzt, aber neuer Token nicht holbar → Login-Maske erzwingen. */
  onSessionLost?: () => void;
}

type PbAuthLike = {
  message?: string;
  token?: string;
  record?: Record<string, unknown>;
};

/**
 * PB invalidiert Auth-Tokens bei Passwortwechsel. PATCH liefert i. d. R. nur den Record.
 * Neuen Token aus der Antwort übernehmen, sonst still mit neuem PW re-auth (ohne Login-UI).
 */
async function adoptSessionAfterPasswordChange(
  pbBase: string,
  email: string,
  newPassword: string,
  patchBody: PbAuthLike & Record<string, unknown>,
  fallbackCustomer: CustomerRecord,
): Promise<{ ok: true; customer: CustomerRecord } | { ok: false; message: string }> {
  const fromPatchToken =
    typeof patchBody.token === 'string' && patchBody.token.trim() ? patchBody.token.trim() : '';
  if (fromPatchToken) {
    const recordSrc =
      patchBody.record && typeof patchBody.record === 'object'
        ? patchBody.record
        : typeof patchBody.id === 'string'
          ? patchBody
          : null;
    const updated = recordSrc?.id
      ? buildCustomerRecord(recordSrc)
      : { ...fallbackCustomer, must_change_password: false };
    persistPortalPbSession(fromPatchToken, pbBase);
    persistPortalCustomer(updated);
    return { ok: true, customer: updated };
  }

  const authRes = await fetch(`${pbBase}/api/collections/customers/auth-with-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ identity: email, password: newPassword }),
  });
  let authData: PbAuthLike = {};
  try {
    authData = (await authRes.json()) as PbAuthLike;
  } catch {
    authData = {};
  }
  const nextToken = typeof authData.token === 'string' ? authData.token.trim() : '';
  if (!authRes.ok || !nextToken || !authData.record?.id) {
    return {
      ok: false,
      message:
        authData.message ||
        'Passwort wurde gesetzt, aber die Sitzung konnte nicht fortgesetzt werden. Bitte mit dem neuen Passwort anmelden.',
    };
  }
  const updated = buildCustomerRecord(authData.record);
  persistPortalPbSession(nextToken, pbBase);
  persistPortalCustomer(updated);
  return { ok: true, customer: updated };
}

export const MustChangePasswordOverlay: React.FC<MustChangePasswordOverlayProps> = ({
  customer,
  onComplete,
  onSessionLost,
}) => {
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [showPwNew, setShowPwNew] = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (pwNew !== pwConfirm) {
      setError('Neues Passwort und Bestätigung stimmen nicht überein.');
      return;
    }
    if (pwNew.length < 8) {
      setError('Neues Passwort mindestens 8 Zeichen.');
      return;
    }
    setLoading(true);
    try {
      const token = getPortalPbToken();
      if (!token) {
        setError('Sitzung abgelaufen. Bitte erneut anmelden.');
        return;
      }
      const oldPassword =
        typeof sessionStorage !== 'undefined'
          ? sessionStorage.getItem(PORTAL_TEMP_LOGIN_PW_KEY) || ''
          : '';
      const body: Record<string, string | boolean> = {
        password: pwNew,
        passwordConfirm: pwConfirm,
        must_change_password: false,
      };
      if (oldPassword) {
        body.oldPassword = oldPassword;
      }
      const pbBase = getPortalPocketBaseUrl();
      const res = await fetch(
        `${pbBase}/api/collections/customers/records/${customer.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(body),
        },
      );
      const data = (await res.json()) as PbAuthLike & Record<string, unknown>;
      if (!res.ok) {
        setError(data.message || 'Passwort konnte nicht gesetzt werden.');
        return;
      }
      sessionStorage.removeItem(PORTAL_TEMP_LOGIN_PW_KEY);

      const session = await adoptSessionAfterPasswordChange(
        pbBase,
        customer.email,
        pwNew,
        data,
        { ...customer, must_change_password: false },
      );
      if (session.ok === false) {
        // PW ist gesetzt — alter Token tot; Login-Maske erzwingen
        clearPortalPbSession();
        setError(session.message);
        onSessionLost?.();
        return;
      }
      onComplete(session.customer);
    } catch {
      setError('Verbindungsfehler. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/95 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="must-change-pw-title"
    >
      <div className="w-full max-w-md bg-slate-900/95 border border-white/[0.08] rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-amber-400" aria-hidden />
          </div>
          <div>
            <h2 id="must-change-pw-title" className="text-lg font-bold text-white">
              Passwort festlegen
            </h2>
            <p className="text-slate-400 text-sm">{BRAND_PORTAL}</p>
          </div>
        </div>

        <p className="text-slate-300 text-sm mb-6 leading-relaxed">
          Aus Sicherheitsgründen müssen Sie beim ersten Login ein eigenes Passwort wählen, bevor Sie
          den Arbeitsplatz nutzen können.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">
              Neues Passwort
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type={showPwNew ? 'text' : 'password'}
                value={pwNew}
                onChange={(e) => setPwNew(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-10 pr-11 py-3 text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowPwNew((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white p-1"
                aria-label={showPwNew ? 'Passwort verbergen' : 'Passwort anzeigen'}
              >
                {showPwNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5 block">
              Passwort bestätigen
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type={showPwConfirm ? 'text' : 'password'}
                value={pwConfirm}
                onChange={(e) => setPwConfirm(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="w-full bg-slate-800 border border-slate-700 rounded-xl pl-10 pr-11 py-3 text-white text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowPwConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white p-1"
                aria-label={showPwConfirm ? 'Passwort verbergen' : 'Passwort anzeigen'}
              >
                {showPwConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-bold py-3 rounded-xl transition-all flex items-center justify-center gap-2 mt-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Wird gespeichert…
              </>
            ) : (
              'Passwort speichern und fortfahren'
            )}
          </button>
        </form>

        <p className="text-center text-slate-600 text-xs mt-6 flex items-center justify-center gap-1">
          <Shield className="w-3 h-3 text-emerald-500" />
          Verschlüsselte Verbindung · EU-Server
        </p>
      </div>
    </div>
  );
};
